import React, { useState, useEffect } from 'react'
import { useParams, useHistory, Redirect } from 'react-router-dom'
import { view } from '@risingstack/react-easy-state'
import styled from 'styled-components'
import { PlusOutlined } from '@ant-design/icons'
import { Button, Input, message, Row, Col, Select } from 'antd'
import dayjs from 'dayjs'

import StyledBox from 'components/common/styled-components/StyledBox'
import ConditionRow from 'components/budget-analysis/ConditionRow'
import globalStore from 'store/index'

import BudgetCriteriaHeadsService from 'services/users/budget-criteria-heads.service'
import BudgetValuesService from 'services/users/budget-values.service'
import { DatePicker } from '../../../../node_modules/antd/lib/index'
import moment from '../../../../node_modules/moment/moment'
import { changeTitle, setSmartBudgetsFavicon, setHireReviewFavicon } from 'utils/header-meta'
import LABELS from 'constants/labels'
import { getCurrentFiscalDateRange } from 'utils/dateRange'

const localizedFormat = require('dayjs/plugin/localizedFormat')
dayjs.extend(localizedFormat)

const StyledConfigurationFormBox = styled.div`
	& .input-box {
		margin: 10px 0px;
	}
	& .action-buttons.submit {
		margin-top: 20px;
	}
`

const blankCondition = {
	field: null,
	operator: null,
	values: null,
}

const BudgetValuesEditPage = () => {
	const history = useHistory()
	// const pageState = globalStore.ui.configuration.budgetValues.edit
	const { budgetValueId, budgetHeadId } = useParams()
	const [criteria, setCriteria] = useState([[blankCondition]])
	const [createButtonLoading, setCreateButtonLoading] = useState(false)
	const [inputValues, setInputValues] = useState({})
	const [budgetCriteriaHeads, setBudgetCriteriaHeads] = useState([[]])
	const [budgetHeadName, setBudgetHeadName] = useState('')
	const [rate, setRate] = useState(null)
	const [count, setCount] = useState(1)
	const [total, setTotal] = useState(null)
	const [frequency, setFrequency] = useState('annually')
	const [annualizedRate, setAnnualizedRate] = useState(0)
	const [proportioned, setProportioned] = useState(1)
	const dateFormat = 'DD-MMM-YYYY'
	const [dateRange, setDateRange] = useState(getCurrentFiscalDateRange)
	const [criteriaChildren, setCriteriaChildren] = useState([])
	const enableAllocationFeature = globalStore?.currentOrganization?.enable_allocation_feature

	//Change title and favicon for SmartBudgets
	useEffect(() => {
		changeTitle(LABELS.smartBudgetPageTitle)
		setSmartBudgetsFavicon()

		return () => {
			changeTitle(LABELS.defaultPageTitle)
			setHireReviewFavicon()
		}
	}, [])

	useEffect(() => {
		if (criteriaChildren.length) {
			const updateCriteriaData = criteria[0].map((data) => {
				if (criteriaChildren.some((items) => items.name === data.field)) {
					return { ...data, values: null, operator: null }
				}
				return data
			})
			setCriteria([updateCriteriaData])
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [criteriaChildren])

	useEffect(() => {
		const fetchBudgetValue = async () => {
			const pageState = globalStore.ui.configuration.budgetValues.edit

			await BudgetValuesService.show({ pageState, budgetValueId })

			const { serverStatus, budget_value, budgetValueErrors } = pageState

			if (serverStatus.status !== 200) {
				message.error('Oops!! something went wrong')
				return
			}

			if (budgetValueErrors) {
				message.error(budgetValueErrors || "Coundn't fetch minimum wage")
				return
			}

			const {
				currency,
				frequency,
				rate,
				criteria,
				budget_head_name,
				start_date,
				end_date,
				unit,
				total_cost_number,
				annualized_amount,
			} = budget_value
			const newCriteria = criteria?.length > 0 ? criteria : [[blankCondition]]
			setCriteria(newCriteria)

			setInputValues({
				currency,
				frequency,
				rate,
				unit,
				total_cost: total_cost_number,
				start_date,
				end_date,
				annualized_amount,
			})

			const newFrequency = frequency ? frequency : 'annually'
			const newAnnualizedAmount = annualized_amount
				? annualized_amount
				: getAnnualizedRate(rate, newFrequency)

			setBudgetHeadName(budget_head_name)

			setCount(unit)
			setRate(rate)
			setTotal(total_cost_number)
			setFrequency(newFrequency)
			setProportioned(getProportionedFactor(moment(start_date), moment(end_date)))
			setAnnualizedRate(newAnnualizedAmount)
			setDateRange([moment(start_date), moment(end_date)])
		}
		fetchBudgetValue()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [budgetValueId, budgetHeadId])

	useEffect(() => {
		const pageState = globalStore.ui.configuration.budgetCriteriaHeads.criteria_head_list

		const fetchAllFieldsData = async () => {
			await BudgetCriteriaHeadsService.criteriaHeadList({ pageState })
			const { budget_criteria_heads } = pageState

			if (!budget_criteria_heads) {
				message.error('Budget criteria not found')
				return
			}
			setBudgetCriteriaHeads(budget_criteria_heads)
		}

		fetchAllFieldsData()
	}, [])

	const handleInputChange = (e, inputType) => {
		let newInputValues = null

		if (inputType === 'frequency') {
			newInputValues = { ...inputValues, [inputType]: e }
			setFrequency(e)
			setAnnualizedRate(getAnnualizedRate(rate, e))
			setTotal(getTotal(count, rate * getFrequencyDays(e), proportioned, e))
		} else {
			newInputValues = { ...inputValues, [inputType]: e.target.value }
		}

		if (inputType === 'rate') {
			setRate(e.target.value)
			setTotal(
				getTotal(count, getAnnualizedRate(e.target.value, frequency), proportioned, frequency)
			)
			setAnnualizedRate(getAnnualizedRate(e.target.value, frequency))
		}

		if (inputType === 'unit') {
			setCount(e.target.value)
			setTotal(getTotal(e.target.value, annualizedRate, proportioned, frequency))
		}

		if (inputType === 'total_cost') {
			setTotal(e.target.value)
			setRate(e.target.value / count)
			setAnnualizedRate(getAnnualizedRate(e.target.value / count, frequency))
		}

		setInputValues(newInputValues)
	}

	const getTotal = (count, annualizedRate, proportioned, frequency) => {
		if (frequency === 'one_time') {
			return count * annualizedRate
		} else {
			return count * annualizedRate * proportioned
		}
	}

	const getProportionedFactor = (startDate, endDate) => {
		return Math.round((getPeriodDays(startDate, endDate) / 365) * 100) / 100
	}

	const getAnnualizedRate = (rate, frequency) => {
		return rate * getFrequencyDays(frequency)
	}

	const getFrequencyDays = (frequency) => {
		if (frequency === 'daily') {
			return 365.0
		} else if (frequency === 'half_yearly') {
			return 2 // 2.027
		} else if (frequency === 'quarterly') {
			return 4 // 4.05
		} else if (frequency === 'monthly') {
			return 12 // 12.16
		} else if (frequency === 'weekly') {
			return 52 // 52.14
		} else if (frequency === 'annually') {
			return 1
		} else if (frequency === 'one_time') {
			return 1
		}
	}

	const getPeriodDays = (startDate, endDate) => {
		const oneDay = 24 * 60 * 60 * 1000
		const days = Math.round(Math.abs((endDate - startDate) / oneDay))
		return days + 1
	}

	const handleAddNewCondition = (conditionSetIndex, parentConditionIndex) => {
		setCriteria((prevCriteria) => {
			const newCriteria = prevCriteria.map((conditionSet, conSetIndex) => {
				if (conSetIndex !== conditionSetIndex) return conditionSet
				const newConditionSet = [...conditionSet]
				newConditionSet.splice(parentConditionIndex + 1, 0, blankCondition)
				return newConditionSet
			})

			return newCriteria
		})
	}

	const handleRemoveCondition = (conditionSetIndex, deletedConditionIndex) => {
		setCriteria((prevCriteria) => {
			const newCriteria = prevCriteria.map((conditionSet, conSetIndex) => {
				if (conSetIndex !== conditionSetIndex) return conditionSet
				const newConditionSet = [...conditionSet]
				newConditionSet.splice(deletedConditionIndex, 1)
				return newConditionSet
			})

			return newCriteria
		})
	}

	const handleUpdateConditionValues = ({ conditionSetIndex, conditionIndex, condition }) => {
		if (!condition) return
		if (!condition.values) return

		setCriteria((prevCriteria) => {
			const newCriteria = prevCriteria.map((conditionSet, conSetIndex) => {
				if (conSetIndex !== conditionSetIndex) return conditionSet
				const newConditionSet = [...conditionSet]
				return newConditionSet.map((c, index) => {
					if (index !== conditionIndex) return c

					return condition
				})
			})

			return newCriteria
		})
	}

	const handleDateChange = (range) => {
		if (range) {
			setDateRange([range[0], range[1]])

			setProportioned(getProportionedFactor(range[0], range[1]))

			setInputValues({
				...inputValues,
				start_date: dateRange[0],
				end_date: dateRange[1],
			})
			setTotal(
				getTotal(count, annualizedRate, getProportionedFactor(range[0], range[1]), frequency)
			)
		}
	}

	const handleUpdate = async () => {
		// if (!inputValues["name"]) {
		//   message.error("Name can't be blank")
		//   return
		// }
		if (!inputValues['currency']) {
			message.error("Currency can't be blank")
			return
		}
		if (!inputValues['rate']) {
			message.error("Rate can't be blank")
			return
		}
		if (!inputValues['unit']) {
			message.error("Unit can't be blank")
			return
		}
		if (!inputValues['start_date']) {
			message.error("Start Date can't be blank")
			return
		}
		if (!inputValues['end_date']) {
			message.error("End Date can't be blank")
			return
		}
		if (!inputValues['frequency']) {
			message.error("Frequency can't be blank")
			return
		}

		inputValues['rate'] = rate
		inputValues['unit'] = count
		inputValues['total_cost'] = total
		inputValues['start_date'] = dateRange[0].format()
		inputValues['end_date'] = dateRange[1].format()
		inputValues['frequency'] = frequency
		inputValues['annualized_amount'] = annualizedRate

		const pageState = {}
		const postData = {
			budget_value: {
				criteria: criteria.filter((c) => c.length > 0),
				...inputValues,
			},
		}

		setCreateButtonLoading(true)
		await BudgetValuesService.update({
			pageState,
			values: postData,
			budgetValueId,
			budgetHeadId,
		})
		setCreateButtonLoading(false)

		const { serverStatus, budgetValueErrors } = pageState

		if (budgetValueErrors) {
			message.error(budgetValueErrors || 'Failed to update budget Value')
			return
		}

		if (serverStatus.status === 200) {
			message.success('Planned Budget updated.')
			history.push(`/configuration/budget-heads/${budgetHeadId}/budget-values/${budgetValueId}`)
			setCriteria([[blankCondition]])
			setInputValues({})

			return
		}

		message.error('Oops!! something went wrong')
	}

	const renderPageContent = () => {
		return (
			<StyledConfigurationFormBox>
				<h1>Planned Value for {budgetHeadName}</h1>
				<Row gutter={20}>
					<Col span={16}>
						<div className='input-box'>
							<div>
								<strong>Period</strong>
							</div>
							<DatePicker.RangePicker
								format={dateFormat}
								value={dateRange} // fix this
								onChange={handleDateChange}
							/>
						</div>
					</Col>
					<Col span={8} pull={2}>
						<div className='input-box'>
							<strong>Proportioned Factor</strong>
							<Input placeholder='Proportioned Factor' value={proportioned} disabled={true} />
						</div>
					</Col>
					<Col span={2}>
						<div className='input-box'>
							<strong>Currency</strong>
							<Input
								placeholder='Currency'
								value={inputValues['currency']}
								onChange={(e) => handleInputChange(e, 'currency')}
							/>
						</div>
					</Col>
					<Col span={4}>
						<div className='input-box'>
							<strong>Count</strong>
							<Input
								placeholder='Enter Count'
								value={count || inputValues['unit']}
								onChange={(e) => handleInputChange(e, 'unit')}
							/>
						</div>
					</Col>
					<Col span={4}>
						<div className='input-box'>
							<strong>Rate</strong>
							<Input
								placeholder='Enter Rate'
								value={rate || inputValues['rate']}
								onChange={(e) => handleInputChange(e, 'rate')}
							/>
						</div>
					</Col>
					<Col span={4}>
						<div className='input-box'>
							<strong>Frequency</strong>
							<Select
								style={{ display: 'block' }}
								placeholder='Choose frequency'
								value={frequency}
								onChange={(value) => handleInputChange(value, 'frequency')}>
								<Select.Option value='daily'>Daily</Select.Option>
								<Select.Option value='weekly'>Weekly</Select.Option>
								<Select.Option value='monthly'>Monthly</Select.Option>
								<Select.Option value='quarterly'>Quarterly</Select.Option>
								<Select.Option value='half_yearly'>Half Yearly</Select.Option>
								<Select.Option value='annually'>Annually</Select.Option>
								<Select.Option value='one_time'>One Time</Select.Option>
							</Select>
						</div>
					</Col>
					<Col span={4}>
						<div className='input-box'>
							<strong>Annualised Rate</strong>
							<Input placeholder='Annualised Rate' value={annualizedRate} disabled={true} />
						</div>
					</Col>
					<Col span={4}>
						<div className='input-box'>
							<strong>Total Cost</strong>
							<Input
								placeholder='Total Cost'
								value={total || inputValues['total_cost']}
								onChange={(e) => handleInputChange(e, 'total_cost')}
							/>
						</div>
					</Col>
				</Row>
				{enableAllocationFeature && (
					<Row gutter={20}>
						<Col span={6}>
							<div className='input-box'>
								<strong>Allocation Percentage</strong>
								<Input
									placeholder='Enter Allocation Percentage'
									value={inputValues['allocation_percentage']}
									onChange={(e) => handleInputChange(e.target.value, 'allocation_percentage')}
								/>
							</div>
						</Col>
						<Col span={6}>
							<div className='input-box'>
								<strong>Increment Percentage</strong>
								<Input
									placeholder='Enter Increment Percentage'
									value={inputValues['increment_percentage']}
									onChange={(e) => handleInputChange(e.target.value, 'increment_percentage')}
								/>
							</div>
						</Col>
					</Row>
				)}
				<div className='input-box'>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
						}}>
						<strong>Criteria</strong>
						{criteria.filter((c) => c.length > 0).length === 0 && (
							<Button shape='circle' onClick={() => setCriteria([[blankCondition]])}>
								<PlusOutlined />
							</Button>
						)}
					</div>
					{criteria.map((conditionSet, conditionSetIndex) => {
						return (
							<div key={conditionSetIndex}>
								{conditionSet.map((condition, conditionIndex) => {
									return (
										<ConditionRow
											criteria={criteria}
											clearChildren={setCriteriaChildren}
											condition={condition}
											key={conditionIndex}
											conditionIndex={conditionSetIndex}
											conditionSetLength={conditionSet.length}
											lastInputGroup={conditionSet.length === conditionIndex + 1}
											firstInputGroup={conditionIndex === 0}
											inputGroupIndex={conditionIndex}
											updateConditionValues={(condition) =>
												handleUpdateConditionValues({
													conditionSetIndex,
													conditionIndex,
													condition,
												})
											}
											addNewCondition={() =>
												handleAddNewCondition(conditionSetIndex, conditionIndex)
											}
											removeCondition={() =>
												handleRemoveCondition(conditionSetIndex, conditionIndex)
											}
											budgetCriteriaHeads={budgetCriteriaHeads}
										/>
									)
								})}
							</div>
						)
					})}
				</div>
				{/* {<div className="input-box">
          <strong>Criteria</strong>
          {criteria.map((c, index) => (
            <ConditionRow
              key={index}
              criteria={c}
              lastInputGroup={criteria.length === index + 1}
              firstInputGroup={index === 0}
              inputGroupIndex={index}
              updateCriteriaValues={(values) =>
                handleUpdateCriteriaValues(values, index)
              }
              addNewCriteria={handleAddNewCriteria}
              removeCriteria={() => handleRemoveCondition(index)}
            />
          ))}
        </div>} */}
				<div className='action-buttons submit'>
					<Button type='primary' loading={createButtonLoading} onClick={handleUpdate}>
						Update
					</Button>
				</div>
			</StyledConfigurationFormBox>
		)
	}

	const accessibleFeatures = globalStore.currentUser?.accessible_features || []

	if (
		accessibleFeatures.length &&
		!['criteria-edit', 'smart-budget'].every((featureKey) =>
			accessibleFeatures.includes(featureKey)
		)
	)
		return <Redirect to={`/configuration/budget-values/${budgetValueId}`} />

	return (
		<div className='page-content dashboard-page'>
			<StyledBox>{renderPageContent()}</StyledBox>
		</div>
	)
}

export default view(BudgetValuesEditPage)
